import { useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import Twemoji from "react-twemoji";
import { ScopeType } from "sockapi/scope-types";
import { TeamPuzzleSockAPIScopeState } from "sockapi/scope-specs/team-spec";
import {
  useTeamId,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import {
  useGlobalScopeStatePosthuntable,
  useScopeStateResolvablePosthuntable,
  useScopeStateResolvable,
  useTeamScopeState,
  usePuzzleDisplayNames,
} from "stores/ScopeStateStore";
import { useNavStore } from "stores/NavStore";
import { ShortDate, Duration } from "components/dates";
import { PuzzleLink, Link } from "components/Link";
import { LargeSpinner } from "components/Loading";
import { Paper } from "components/Material";
import SortableTable from "components/SortableTable";
import { TeamDisplayNamePreloaded } from "components/TeamDisplayName";
import {
  useShowStats,
  useShowHuntStats,
} from "components/PuzzlePage/PuzzlePageToolbar";
import { PuzzlePageWrapper } from "pages/PuzzlePage";
import { clsObj, displayNameOrder } from "client-util/util";
import { PuzzleType } from "sockapi/puzzle-data";

function AdminStatsDisplay() {
  const { puzName } = useOutletContext<TeamPuzzleSockAPIScopeState>();
  const adminStatsState = useScopeStateResolvable(ScopeType.ADMIN_STATS, {
    puzName,
  });

  if (adminStatsState === null) return null;

  const answers = Object.entries(adminStatsState)
    .map(([key, count]) => {
      const [answer, isCorrect, isWrong] = JSON.parse(key) as [
        string,
        boolean,
        boolean,
      ];
      const sigil = isCorrect ? "✅️" : isWrong ? "❌️" : "💬️";
      return { key, answer, sigil, count };
    })
    .sort((a, b) => a.answer.localeCompare(b.answer))
    .sort((a, b) => b.count - a.count);

  return (
    <SortableTable
      spec={{
        columns: [
          {
            label: "Answer",
            sortFunc: (a, b) => a.answer.localeCompare(b.answer),
            width: "80%",
          },
          {
            label: "Status",
            sortFunc: (a, b) => a.sigil.localeCompare(b.sigil),
            width: "10%",
          },
          {
            label: "Count",
            sortFunc: (a, b) => a.count - b.count,
            width: "10%",
          },
        ],
        renderRow: ({ key, answer, sigil, count }) => (
          <tr key={key}>
            <td>{answer}</td>
            <td>
              <Twemoji>{sigil}</Twemoji>
            </td>
            <StatCell value={count} />
          </tr>
        ),
      }}
      records={answers}
      className="list-table"
    />
  );
}

export function StatsDisplay() {
  const myTeamId = useTeamId();
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const puzData = useOutletContext<TeamPuzzleSockAPIScopeState>();
  const { puzName, slug } = puzData;
  const teamsResult = useGlobalScopeStatePosthuntable(
    ScopeType.TEAM_LIST,
    true,
    { hasPosthunt: true }
  );
  const statsResult = useScopeStateResolvablePosthuntable(
    ScopeType.STATS,
    { puzName },
    { hasPosthunt: true }
  );
  const showStats = useShowStats(puzData);
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);

  useEffect(() => {
    if (showStats ?? true) return;
    setRedirectRoute(
      slug ? { route: "puzzle", params: { slug } } : { route: "puzzles" }
    );
  }, [showStats, slug, setRedirectRoute]);

  if (!(showStats ?? false) || !teamsResult?.success || !statsResult?.success)
    return <LargeSpinner />;

  const statsState = statsResult.state;
  let totalGuesses = 0;
  for (const num of Object.values(statsState.guesses)) totalGuesses += num;
  const teams = Object.entries(statsState.solves)
    .map(([teamId, solveTime]) => {
      const team = teamsResult.state[teamId];
      return {
        team,
        myTeamId,
        unlockTime: statsState.unlocks[teamId] ?? 0,
        solveTime,
        guesses: statsState.guesses[teamId] ?? 1,
      };
    })
    .filter(({ team }) => team !== undefined && !team.isHidden)
    .sort((a, b) => a.solveTime - b.solveTime);

  return (
    <div className="puzzle-page-body">
      <Link
        route="hunt_stats"
        style={{ alignSelf: "center", textDecoration: "none" }}
      >
        <Paper rotate={0}>Back to Hunt Stats</Paper>
      </Link>
      <div className="info-row">
        <div>
          <StatEntry label="Total guesses" value={totalGuesses} />
          <StatEntry
            label="Total solves"
            value={Object.keys(statsState.solves).length}
          />
        </div>
        <div>
          {/*
          <StatEntry
            label="Canned hints found"
            value={statsState.getCannedHints}
          />
          <StatEntry
            label="Canned hints opened"
            value={statsState.openCannedHints}
          />
          */}
          <StatEntry
            label="Teams unlocked"
            value={Object.keys(statsState.unlocks).length}
          />
          <StatEntry label="Hints asked" value={statsState.hints} />
        </div>
      </div>
      <SortableTable
        spec={{
          columns: [
            {
              label: "Team",
              sortFunc: (a, b) =>
                a.team.displayName.localeCompare(b.team.displayName),
              width: "50%",
            },
            {
              label: "Guesses",
              sortFunc: (a, b) => a.guesses - b.guesses,
              width: "10%",
            },
            {
              label: "Unlock time",
              sortFunc: (a, b) => a.unlockTime - b.unlockTime,
              width: "15%",
            },
            {
              label: "→",
              sortFunc: (a, b) =>
                a.solveTime - a.unlockTime - (b.solveTime - b.unlockTime),
              width: "10%",
            },
            {
              label: "Solve time",
              sortFunc: (a, b) => a.solveTime - b.solveTime,
              width: "15%",
            },
          ],
          renderRow: ({ team, myTeamId, unlockTime, solveTime, guesses }) => (
            <tr
              key={team.teamId}
              className={clsObj({ "current-team": myTeamId === team.teamId })}
            >
              <td>
                <TeamDisplayNamePreloaded teamData={team} />
              </td>
              <StatCell value={guesses} />
              <td>
                <ShortDate timestamp={unlockTime} />
              </td>
              <td>
                <Duration from={unlockTime} to={solveTime} />
              </td>
              <td>
                <ShortDate timestamp={solveTime} />
              </td>
            </tr>
          ),
        }}
        records={teams}
        className="list-table"
      />
      {shouldDisplayAdmin && <AdminStatsDisplay />}
    </div>
  );
}

export function HuntStatsPage() {
  const myTeamId = useTeamId();
  const teamScopeState = useTeamScopeState();
  const displayNames = usePuzzleDisplayNames();
  const huntStatsResult = useGlobalScopeStatePosthuntable(
    ScopeType.HUNT_STATS,
    true,
    { hasPosthunt: true }
  );
  const showHuntStats = useShowHuntStats();
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);

  useEffect(() => {
    if (showHuntStats ?? true) return;
    setRedirectRoute({ route: "puzzles" });
  }, [showHuntStats, setRedirectRoute]);

  if (
    !(showHuntStats ?? false) ||
    teamScopeState === null ||
    displayNames === null ||
    !huntStatsResult?.success
  )
    return <LargeSpinner />;

  const huntStatsState = huntStatsResult.state;
  const topStats = {
    ...huntStatsState,
    solves: 0,
    guesses: 0,
    hints: 0,
    getCannedHints: 0,
    openCannedHints: 0,
    metaSolves: 0,
  };
  for (const summary of Object.values(huntStatsState.puzzles)) {
    topStats.solves += summary.solves;
    topStats.guesses += summary.guesses;
    topStats.hints += summary.hints;
    topStats.getCannedHints += summary.getCannedHints;
    topStats.openCannedHints += summary.openCannedHints;
    topStats.metaSolves += summary.isMeta ? summary.solves : 0;
  }
  const puzzles = Object.entries(huntStatsState.puzzles)
    .map(([puzName, puzStats]) => {
      const puzData = teamScopeState.puzzles[puzName];
      const name = displayNames[puzName] ?? "???";
      const { order, isMeta } = puzStats;
      const numbers = [
        puzStats.unlocks,
        puzStats.guesses,
        puzStats.solves,
        puzStats.hints,
        puzStats.unhinted,
        puzStats.hinted,
        puzStats.unsolved,
      ];
      return { puzName, puzData, myTeamId, name, order, isMeta, numbers };
    })
    .filter(
      ({ puzData }) =>
        puzData !== undefined && puzData.type === PuzzleType.PUZZLE
    )
    .sort((a, b) => a.order - b.order);

  return (
    <PuzzlePageWrapper>
      <div className="centered-page">
        <h1>Hunt Stats</h1>
        <div className="info-row">
          <div>
            <StatEntry label="Total teams" value={topStats.teams} />
            <StatEntry label="Total participants" value={topStats.members} />
            <StatEntry label="Total guesses" value={topStats.guesses} />
            <StatEntry label="Total solves" value={topStats.solves} />
          </div>
          <div>
            {/*
            <StatEntry
              label="Canned hints found"
              value={topStats.getCannedHints}
            />
            <StatEntry
              label="Canned hints opened"
              value={topStats.openCannedHints}
            />
            <StatEntry label="Hint tokens found" value={topStats.hintTokens} />
            */}
            <StatEntry label="Metas solved" value={topStats.metaSolves} />
            <StatEntry label="Hints asked" value={topStats.hints} />
            <StatEntry
              label="FAIR PLAY guesses"
              value={topStats.fairplayGuesses}
            />
            <StatEntry
              label="FAIR PLAY solves"
              value={topStats.fairplaySolves}
            />
          </div>
        </div>
        <SortableTable
          spec={{
            columns: [
              {
                label: "Puzzle",
                sortFunc: (a, b) => displayNameOrder(a.name, b.name),
              },
              ..."Unlocks Guesses Solves Hints Unhinted Hinted Unsolved"
                .split(" ")
                .map((label, i) => ({
                  label,
                  sortFunc: (
                    a: { numbers: number[] },
                    b: { numbers: number[] }
                  ) => a.numbers[i] - b.numbers[i],
                })),
            ],
            renderRow: ({ puzName, name, isMeta, numbers }) => (
              <tr key={puzName} style={isMeta ? { fontWeight: "bold" } : {}}>
                <td>
                  <PuzzleLink puzName={puzName} subpage="stats">
                    {name}
                  </PuzzleLink>
                </td>
                {numbers.map((num, i) => (
                  <StatCell key={i} value={num} />
                ))}
              </tr>
            ),
          }}
          records={puzzles}
          className="list-table"
        />
      </div>
    </PuzzlePageWrapper>
  );
}

function StatEntry({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <b>{label}:</b> {value.toLocaleString()}
    </div>
  );
}

function StatCell({ value }: { value: number }) {
  return <td style={{ fontVariant: "tabular-nums" }}>{value}</td>;
}
